import { useEffect, useMemo, useRef, useState } from 'react';
import { FaLock, FaPen, FaPlus, FaSearch, FaSyncAlt, FaTrash, FaUnlock } from 'react-icons/fa';
import { toast } from 'react-toastify';
import axiosInstance from '../axiosInstance';
import { API_URL_All } from '../api';
import * as general from '../libs/general';
import AdLayout from '../components/AdLayout';
import DataTable from '../components/ui/DataTable';
import Paging from '../components/Paging';
import { CheckingTicket, EyeIcon } from '../components/tickets/Tickets';
import TthOrderEditor from '../components/orders/TthOrderEditor';
import TthOrderHistoryInfo from '../components/orders/TthOrderHistoryInfo';

const TTH_STATUS_OPTIONS = ['Đơn mới', 'Khách CK', 'Thành công', 'Đơn hủy'];
const TTH_RATE_STATUS_OPTIONS = ['Chưa đối soát', 'Vào phiếu', 'Đã đối soát'];

/**
 * Màn "Đơn TTH" — entity mới hoàn toàn (không tái dùng ImportOrder), nhưng layout/CSS/lib-filter
 * mirror y hệt màn "Đơn hàng" hiện có (OrderAdmin.jsx/NormalInvoices.jsx): cùng class
 * table-responsive/web-table/normal-form/table-primary/border-normal, cùng cơ chế filter-ib (icon
 * filter trong <th>, đọc window.initializeFilterValue/general.getFormData/getSession), cùng cơ chế
 * checkbox chọn dòng + toolbar "Cập nhật nhanh" hàng loạt, cùng icon sửa/khóa/mắt (lịch sử + comment).
 * Thứ tự cột theo đúng yêu cầu: [chọn] → Mã đơn → Ngày → Line → Số lượng Tệ → Tỷ giá KH → Tỷ giá ĐT →
 * VNĐ KH → VNĐ ĐT → Lưu trình → Mã KH → Mã ĐT → Đối soát ĐT → [Thao tác].
 */
function TthOrderAdmin() {
  const [pageData, setPageData] = useState({ content: [] });
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [, setParams] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const hasRun = useRef(false);

  const [editorShow, setEditorShow] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [selectedIds, setSelectedIds] = useState([]);
  const [historyOrderId, setHistoryOrderId] = useState(null);

  const search = async (filter) => {
    if (window.callingTthOrders) return;
    window.callingTthOrders = true;
    setIsLoading(true);
    setErrorMessage('');

    var nextParams = general.getSession('search_tth_orders');
    var formData = general.getFormData('#tth_order_filter');
    Object.assign(nextParams, formData);

    if (!hasRun.current) {
      hasRun.current = true;
      var oldParams = general.getSession('search_tth_orders');
      Object.assign(nextParams, oldParams);
      setSearchQuery(nextParams.keyword || '');
    }
    if (filter) Object.assign(nextParams, filter);

    general.setSession('search_tth_orders', nextParams);
    setParams(nextParams);
    if (window.initializeFilterValue) window.initializeFilterValue(nextParams);

    var filteredParams = Object.fromEntries(
      Object.entries(nextParams).filter(([key, v]) => v !== undefined && v !== '' && key + v !== 'page0' && key + v !== 'size550')
    );

    try {
      const res = await axiosInstance.get('/api/tth-orders/searchTthOrders', { params: filteredParams });
      if (res.data?.success === false) {
        setErrorMessage(res.data.message || 'Không tải được dữ liệu');
        setPageData({ content: [] });
      } else {
        setPageData(res.data);
      }
    } catch (err) {
      setErrorMessage(err.message);
      setPageData({ content: [] });
    } finally {
      window.callingTthOrders = false;
      setIsLoading(false);
      setSelectedIds([]);
    }
  };

  useEffect(() => { search(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    search({ page: 0, keyword: e.target.value });
  };
  const handleSearchClick = () => search({ page: 0 });
  const handlePageChange = (p) => search({ page: p - 1 });
  const handleItemsPerPageChange = (e) => search({ page: 0, size: parseInt(e.target.value, 10) });

  const openCreate = () => { setEditingId(null); setEditorShow(true); };
  const openEdit = (id) => { setEditingId(id); setEditorShow(true); };

  const handleDelete = async (item) => {
    if (item.locked) {
      toast.warning('Đơn đã khóa (đã đối soát), không thể xóa.');
      return;
    }
    general.openConfirm(async () => {
      try {
        const res = await axiosInstance.put(`/api/tth-orders/deleteTthOrder/${item.id}`);
        if (!res.data?.success) {
          toast.error(res.data?.message || 'Xóa thất bại');
          return;
        }
        toast.success(res.data.message || 'Đã xóa');
        search();
      } catch (err) {
        toast.error(err.message);
      }
    });
  };

  const content = pageData.page?.content || pageData.content || [];

  const pageTotals = useMemo(() => {
    const sum = content.reduce((acc, item) => {
      const quantity = Number(item.quantity);
      const amountCustomer = Number(item.amountCustomer);
      const amountPartner = Number(item.amountPartner);
      if (!Number.isNaN(quantity)) acc.quantity += quantity;
      if (!Number.isNaN(amountCustomer)) acc.amountCustomer += amountCustomer;
      if (!Number.isNaN(amountPartner)) acc.amountPartner += amountPartner;
      return acc;
    }, { quantity: 0, amountCustomer: 0, amountPartner: 0 });
    const round2 = (n) => Math.round(n * 100) / 100;
    return {
      quantity: round2(sum.quantity),
      amountCustomer: round2(sum.amountCustomer),
      amountPartner: round2(sum.amountPartner),
    };
  }, [content]);

  // -----------------------------------------------------------------------
  // Chọn dòng + Cập nhật nhanh hàng loạt (mirror OrderAdmin.jsx, rút gọn vì TTH không có sub-detail
  // rows — chỉ chọn trong phạm vi trang hiện tại, không cross-page như Đơn hàng thường).
  // -----------------------------------------------------------------------
  const handleSelectAll = () => {
    const checkAllTag = document.querySelector('.tth_check_all');
    if (checkAllTag?.checked) {
      setSelectedIds(content.map((item) => item.id));
    } else {
      setSelectedIds([]);
    }
  };
  const handleSelectOne = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const submitBulkUpdate = async (body) => {
    try {
      const res = await axiosInstance.put('/api/tth-orders/changeTthOrderStatus', body);
      if (!res.data?.success) {
        toast.error(res.data?.message || 'Cập nhật thất bại');
        return;
      }
      toast.success(res.data.message || 'Cập nhật thành công');
      setSelectedIds([]);
      search();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message);
    }
  };

  const requireSelection = () => {
    if (selectedIds.length === 0) {
      toast.warning('Vui lòng chọn ít nhất 1 đơn TTH');
      return false;
    }
    return true;
  };

  const handleBulkProgress = (progressName) => {
    if (!requireSelection()) return;
    general.openConfirm(
      () => submitBulkUpdate({ formType: 'progress', listIds: selectedIds, progressName }),
      `Đổi Lưu trình ${selectedIds.length} đơn đã chọn sang "${progressName}"?`,
      'Xác nhận'
    );
  };

  const handleBulkRateStatus = (rateStatusName) => {
    if (!requireSelection()) return;
    general.openConfirm(
      () => submitBulkUpdate({ formType: 'rateStatus', listIds: selectedIds, rateStatusName }),
      `Đổi Trạng thái đối soát ${selectedIds.length} đơn đã chọn sang "${rateStatusName}"?`,
      'Xác nhận'
    );
  };

  const handleToggleLock = (item) => {
    const locked = !item.locked;
    general.openConfirm(
      () => submitBulkUpdate({ formType: 'locking', listIds: [item.id], locked }),
      locked ? 'Khóa đơn hàng này?' : 'Mở khóa đơn hàng này (chỉ áp dụng nếu chưa Đã đối soát)?',
      'Xác nhận'
    );
  };

  /**
   * Click-to-edit ô "Lưu trình" (đúng cơ chế `.text_editor` + hidden `.save_row` button mà
   * OrderAdmin.jsx đang dùng cho cột "Tiến trình"): lib-filter.js tự bắt double-click → hiện dropdown
   * chọn trạng thái từ #list_tth_status → khi chọn 1 dòng, tự gán data-post-name/data-post-value lên
   * nút .save_row rồi bấm hộ → hàm này chỉ đọc 2 attribute đó ra và gọi API.
   */
  const saveDataRow = async (rowId, e) => {
    const name = e.target.getAttribute('data-post-name');
    const value = e.target.getAttribute('data-post-value');
    try {
      const res = await axiosInstance.put('/api/tth-orders/saveTthOrderStatus', { id: rowId, name, code: value });
      if (!res.data?.success) {
        toast.error(res.data?.message || 'Cập nhật trạng thái thất bại');
        return;
      }
      if (res.data.data?.colorCode) {
        const rowTag = document.querySelector(`tr[data-row-id='${rowId}']`);
        const progressTag = rowTag?.querySelector(`td[data-name='${name}']`);
        if (progressTag) progressTag.style.setProperty('--bs-table-bg-type', res.data.data.colorCode);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || err.message);
    }
  };

  return (
    <AdLayout>
      <div className="table-responsive web-table tth-order-screen" data-loading={isLoading}>
        <div className="container-fluid">
          <div className="row bg-dark">
            <div className="col-12 col-md-8 order-3 order-md-1 d-flex p-2">
              <div className="flex-start gap-2">
                <button type="button" className="btn btn-success btn-sm" onClick={openCreate} title="Thêm đơn TTH">
                  <FaPlus /> Đơn TTH
                </button>
                <div className="dropdown">
                  <button className="btn btn-sm btn-primary dropdown-toggle" type="button" data-bs-toggle="dropdown" aria-expanded="false" title="Cập nhật nhanh">
                    <FaSyncAlt />
                  </button>
                  <ul className="dropdown-menu">
                    <li className="dropdown-submenu dropdown-2">
                      <a className="dropdown-item dropdown-toggle rotate-90">Lưu trình</a>
                      <ul className="dropdown-menu">
                        {TTH_STATUS_OPTIONS.map((s) => (
                          <li key={s}><a className="dropdown-item" onClick={() => handleBulkProgress(s)}>{s}</a></li>
                        ))}
                      </ul>
                    </li>
                    <li className="dropdown-submenu dropdown-2">
                      <a className="dropdown-item dropdown-toggle rotate-90">Trạng thái đối soát</a>
                      <ul className="dropdown-menu">
                        {TTH_RATE_STATUS_OPTIONS.map((s) => (
                          <li key={s}><a className="dropdown-item" onClick={() => handleBulkRateStatus(s)}>{s}</a></li>
                        ))}
                      </ul>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="col-7 col-md-2 py-1 fw-bold text-white text-uppercase d-flex align-items-center justify-content-star order-1 order-md-2">
              Quản lý đơn TTH
            </div>
            <div className="col-5 col-md-2 py-1 order-2 order-md-3">
              <form id="tth_order_filter">
                <div className="input-group search-block">
                  <input
                    name="keyword"
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Tìm kiếm..."
                    autoComplete="off"
                    value={searchQuery}
                    onChange={handleSearchChange}
                  />
                  <button onClick={handleSearchClick} className="btn btn-light btn-sm" type="button" title="Tìm kiếm...">
                    <FaSearch />
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        <section className="p-1 tth-order-screen__table">
          <DataTable maxHeight="100%" className="table-normal" data-loading={isLoading}>
            <table className="table table-striped table-bordered border-normal">
              <thead className="table-primary">
                <tr>
                  <th className="sticky text-center" width="30px">
                    <input type="checkbox" title="Chọn tất cả" className="tth_check_all" onChange={handleSelectAll} />
                  </th>
                  <th className="text-center"><span>Mã đơn</span></th>
                  <th className="text-center"><span>Ngày</span></th>
                  <th className="text-center"><span>Line</span></th>
                  <th className="text-center"><span>Số lượng Tệ</span></th>
                  <th className="text-center"><span>Tỷ giá KH</span></th>
                  <th className="text-center"><span>Tỷ giá ĐT</span></th>
                  <th className="text-center"><span>VNĐ KH</span></th>
                  <th className="text-center"><span>VNĐ ĐT</span></th>
                  <th className="text-center">
                    <span>Lưu trình</span>
                    <span className="filter-ib" data-form="tth_order_filter" data-filter="status"
                      data-source-key="list_tth_status" data-min="100px"><i className="filter-i"></i></span>
                  </th>
                  <th className="text-center">
                    <span>Mã KH</span>
                    <span className="filter-ib" data-form="tth_order_filter" data-filter="customerCode"
                      data-source-url={API_URL_All + '/api/users/searchUserCodes'} data-min="100px"><i className="filter-i"></i></span>
                  </th>
                  <th className="text-center">
                    <span>Mã ĐT</span>
                    <span className="filter-ib" data-form="tth_order_filter" data-filter="partnerCode"
                      data-source-url={API_URL_All + '/api/contacts/searchContactCodes'} data-min="100px"><i className="filter-i"></i></span>
                  </th>
                  <th className="text-center">
                    <span>Đối soát ĐT</span>
                    <span className="filter-ib" data-form="tth_order_filter" data-filter="rateStatus"
                      data-source-key="list_tth_rateStatus" data-min="100px"><i className="filter-i"></i></span>
                  </th>
                  <th></th>
                </tr>
              </thead>
                {content.map((item) => (
                  <tbody className="tbody-row" key={item.id}>
                    <tr data-row-id={item.id}>
                      <td className="sticky">
                        <input type="checkbox" checked={selectedIds.includes(item.id)} onChange={() => handleSelectOne(item.id)} />
                      </td>
                      <td className="text-center">
                        <span role="button" className="text-primary" onClick={() => openEdit(item.id)}>
                          {item.orderCode}
                        </span>
                      </td>
                      <td className="text-center" title={general.formatDate(item.createdDate)}>{general.formatTimeShort(item.createdDate)}</td>
                      <td className="text-start">{item.lineName}</td>
                      <td className="text-center">{general.formatDecimal(item.quantity)}</td>
                      <td className="text-center">{general.formatNumber(item.customerRate)}</td>
                      <td className="text-center">{general.formatNumber(item.partnerRate)}</td>
                      <td className="text-center">{general.formatNumber(item.amountCustomer)}</td>
                      <td className="text-center">{general.formatNumber(item.amountPartner)}</td>
                      <td
                        className="text-start text_editor"
                        style={{ '--bs-table-bg-type': item.colorCode }}
                        data-source-key={!item.locked ? 'list_tth_status' : undefined}
                        data-name={!item.locked ? 'status' : undefined}
                        title={item.locked ? item.statusName : `${item.statusName} (bấm đúp để đổi trạng thái)`}
                      >
                        {item.statusName}
                      </td>
                      <td className="text-center">{item.customerCode}</td>
                      <td className="text-center">{item.partnerCode}</td>
                      <td className="text-center">
                        <span className="pe-2">{general.formatNumber(item.amountDiff)}</span>
                        <CheckingTicket checkingId={item.statusRateId} checkingName={item.statusRateName} />
                      </td>
                      <td>
                        <a className="cursor-pointer me-2" onClick={() => openEdit(item.id)} title="Cập nhật đơn hàng">
                          <FaPen className="text-primary" size={15} />
                        </a>
                        <a className="cursor-pointer me-2" onClick={() => handleToggleLock(item)} title={item.locked ? 'Mở khóa đơn hàng' : 'Khóa đơn hàng'}>
                          {item.locked ? <FaLock className="text-danger" size={15} /> : <FaUnlock className="text-success" size={15} />}
                        </a>
                        <span className="view_progress me-2" title="Lịch sử trạng thái" onClick={() => setHistoryOrderId(item.id)}>
                          <EyeIcon />
                        </span>
                        {!item.locked && (
                          <span className="small-btn text-danger" onClick={() => handleDelete(item)}><FaTrash /></span>
                        )}
                        <button type="button" className="save_row d-none" onClick={(e) => saveDataRow(item.id, e)}>Save row</button>
                      </td>
                    </tr>
                  </tbody>
                ))}
                {!isLoading && errorMessage === '' && content.length === 0 && (
                  <tbody><tr><td colSpan="14" className="text-center">Không tìm thấy dữ liệu</td></tr></tbody>
                )}
                {errorMessage !== '' && (
                  <tbody><tr><td colSpan="14" className="text-center">{errorMessage}</td></tr></tbody>
                )}
            </table>
          </DataTable>
        </section>

        <div className="tth-order-screen__footer d-flex justify-content-end align-items-center">
          <Paging
            pageData={pageData}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            tthTotals={content.length > 0 ? pageTotals : null}
          />
        </div>

        <input type="hidden" id="list_tth_status" value="Đơn mới,Khách CK,Thành công,Đơn hủy" />
        <input type="hidden" id="list_tth_rateStatus" value="Chưa đối soát,Vào phiếu,Đã đối soát" />
      </div>

      <TthOrderEditor
        show={editorShow}
        onHide={() => setEditorShow(false)}
        orderId={editingId}
        onSaved={() => search()}
      />

      {historyOrderId && (
        <TthOrderHistoryInfo tthOrderId={historyOrderId} onClose={() => setHistoryOrderId(null)} />
      )}
    </AdLayout>
  );
}

export default TthOrderAdmin;
